"use client";

import { useEffect, useMemo, useState } from "react";
import { FaRegFolder } from "react-icons/fa";
import { FiEdit3 } from "react-icons/fi";
import { IoClose } from "react-icons/io5";
import { MdKeyboardArrowDown } from "react-icons/md";
import { RiDeleteBinLine } from "react-icons/ri";
import { TbNotebook } from "react-icons/tb";
import categoryAndGroupAPI from "../services/categoryAndGroup/categoryAndGroupAPI";
import mainAPI from "../services/main/mainAPI";
import noteAPI from "../services/note/noteAPI";
import ConfirmPopUp from "./popup/confirmPopUp";
import StatusPopUp from "./popup/statusPopUp";
import "react-photo-view/dist/react-photo-view.css";
import { PhotoProvider, PhotoView } from "react-photo-view";
import { formatCompactDateTime, formatCompactDate } from "../utils/formatDatetime";
import { useTranslations, useLocale } from "next-intl";
import {
    getMediaValidationErrorMessage,
    getMediaValidationTitle,
    validateImageFile,
} from "./games/shared/mediaValidation";

export type InformationDetailNote = {
    id: number | string;
    name: string;
    detail: string;
    create_at: string;
};

export type InformationDetailData = {
    uniqueId: string;
    gameUuid?: string;
    gameId?: number | null;
    source: string;
    subject: string;
    category?: string;
    group?: string;
    groupId?: number | null;
    exerciseName: string;
    thumbnailUrl?: string;
    createdAt: string;
    updatedAt: string;
    createdBy: string;
    description: string;
    notes: InformationDetailNote[];
    banAt?: string | null;
    isBanned?: boolean;
};

export type InformationDetailUpdates = {
    exerciseName?: string;
    description?: string;
    category?: string;
    group?: string;
    groupId?: number | string | null;
    thumbnailUrl?: string;
};

type InformationDetailProps = {
    isOpen: boolean;
    detail: InformationDetailData | null;
    onClose: () => void;
    onCreateNote: (detail: InformationDetailData) => void;
    onEditNote: (detail: InformationDetailData, note: InformationDetailNote) => void;
    onDeleteNoteSuccess?: (detailUniqueId: string, noteId: number | string) => void;
    onThumbnailUpdated?: (detailUniqueId: string, nextThumbnailUrl: string) => void;
    onDetailUpdated?: (detailUniqueId: string, updates: InformationDetailUpdates) => void;
};

function DetailLine({ label, value }: { label: string; value: string }) {
    return (
        <div className="rounded-sm bg-white px-3 py-2 text-[#4E4E4E]">
            <div className="grid grid-cols-1 items-start gap-1 text-sm sm:grid-cols-[140px_1fr] sm:gap-2 sm:text-lg">
                <span>{label}</span>
                <span className="wrap-break-word">{value || "-"}</span>
            </div>
        </div>
    );
}

export default function InformationDetail({
    isOpen,
    detail,
    onClose,
    onCreateNote,
    onEditNote,
    onDeleteNoteSuccess,
    onThumbnailUpdated,
    onDetailUpdated,
}: InformationDetailProps) {
    const [deleteTargetNote, setDeleteTargetNote] = useState<InformationDetailNote | null>(null);
    const [isDeletingNote, setIsDeletingNote] = useState(false);
    const [statusPopUp, setStatusPopUp] = useState<{
        title: string;
        message: string;
        type: "info" | "success" | "error";
    } | null>(null);
    const [isThumbnailEditorOpen, setIsThumbnailEditorOpen] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [thumbnailFileName, setThumbnailFileName] = useState("");
    const [thumbnailFile, setThumbnailFile] = useState<File | null>(null);
    const [localThumbnailUrl, setLocalThumbnailUrl] = useState("");
    const [selectedCategory, setSelectedCategory] = useState("");
    const [selectedGroup, setSelectedGroup] = useState("");
    const [categoryOptions, setCategoryOptions] = useState<string[]>([]);
    const [groupOptions, setGroupOptions] = useState<{ id: number | string; name: string }[]>([]);
    const [isCategoryLoading, setIsCategoryLoading] = useState(false);
    const [isGroupLoading, setIsGroupLoading] = useState(false);
    const [isEditingNameAndDesc, setIsEditingNameAndDesc] = useState(false);
    const [editExerciseName, setEditExerciseName] = useState("");
    const [editDescription, setEditDescription] = useState("");
    const [validationMessage, setValidationMessage] = useState<string | null>(null);
    
    const tID = useTranslations("informationDetail");
    const locale = useLocale();
    const noteRows = useMemo(() => detail?.notes ?? [], [detail?.notes]);
    const sourceLabel = String(detail?.source ?? "").toLowerCase() === "sequence" ? tID("exerciseSet") : tID("name");
    const isBannedRow = Boolean(detail?.isBanned || String(detail?.banAt ?? "").trim());

    const pendingThumbnailPreviewUrl = useMemo(() => {
        if (!thumbnailFile) {
            return null;
        }

        return URL.createObjectURL(thumbnailFile);
    }, [thumbnailFile]);

    useEffect(() => {
        return () => {
            if (pendingThumbnailPreviewUrl) {
                URL.revokeObjectURL(pendingThumbnailPreviewUrl);
            }
        };
    }, [pendingThumbnailPreviewUrl]);

    const displayThumbnailUrl = pendingThumbnailPreviewUrl || localThumbnailUrl;

    useEffect(() => {
        setIsThumbnailEditorOpen(false);
        setThumbnailFile(null);
        setThumbnailFileName("");
        setSelectedCategory(String(detail?.category ?? "").trim());
        setSelectedGroup(String(detail?.group ?? "").trim());
        setCategoryOptions([]);
        setGroupOptions([]);
        setIsEditingNameAndDesc(false);
        setEditExerciseName(String(detail?.exerciseName ?? "").trim());
        setEditDescription(String(detail?.description ?? "").trim());
        setLocalThumbnailUrl(String(detail?.thumbnailUrl ?? ""));
    }, [detail?.uniqueId]);

    useEffect(() => {
        setLocalThumbnailUrl(String(detail?.thumbnailUrl ?? ""));
    }, [detail?.uniqueId, detail?.thumbnailUrl]);

    useEffect(() => {
        setSelectedCategory(String(detail?.category ?? "").trim());
        setSelectedGroup(String(detail?.group ?? "").trim());
        setEditExerciseName(String(detail?.exerciseName ?? "").trim());
        setEditDescription(String(detail?.description ?? "").trim());
    }, [
        detail?.uniqueId,
        detail?.category,
        detail?.group,
        detail?.exerciseName,
        detail?.description,
    ]);

    useEffect(() => {
        if (!isOpen) {
            return;
        }

        const prevHtmlOverflow = document.documentElement.style.overflow;
        const prevBodyOverflow = document.body.style.overflow;

        document.documentElement.style.overflow = "hidden";
        document.body.style.overflow = "hidden";

        return () => {
            document.documentElement.style.overflow = prevHtmlOverflow;
            document.body.style.overflow = prevBodyOverflow;
        };
    }, [isOpen]);

    const handleDeleteNote = async () => {
        if (!detail || !deleteTargetNote) {
            return;
        }

        setIsDeletingNote(true);
        const response = await noteAPI.softDelete(deleteTargetNote.id);
        setIsDeletingNote(false);

        if (!response || (response as { status?: string }).status === "failed") {
            setStatusPopUp({
                title: tID("deleteFailed"),
                message: tID("deleteNoteFailed"),
                type: "error",
            });
            return;
        }

        onDeleteNoteSuccess?.(detail.uniqueId, deleteTargetNote.id);
        setDeleteTargetNote(null);
        setStatusPopUp({
            title: tID("success"),
            message: tID("deleteNoteSuccess"),
            type: "success",
        });
    };

    const getTeacherUuidFromLocalStorage = () => {
        try {
            const raw = localStorage.getItem("user");
            if (!raw) {
                return "";
            }

            const parsed = JSON.parse(raw) as { id?: unknown };
            if (!parsed || typeof parsed !== "object") {
                return "";
            }

            if (parsed.id === undefined || parsed.id === null) {
                return "";
            }

            return String(parsed.id).trim();
        } catch {
            return "";
        }
    };

    const mergeWithCurrentValue = (currentValue: string, options: string[]) => {
        const cleaned = options.map((item) => String(item ?? "").trim()).filter(Boolean);
        const merged = currentValue ? [currentValue, ...cleaned] : cleaned;
        return Array.from(new Set(merged));
    };

    const CategoryOptions = async () => {
        if (isCategoryLoading) {
            return;
        }

        const teacherUuid = getTeacherUuidFromLocalStorage();
        if (!teacherUuid) {
            setStatusPopUp({
                title: tID("failed"),
                message: tID("noUserDataForCategory"),
                type: "error",
            });
            return;
        }

        setIsCategoryLoading(true);
        const response = await categoryAndGroupAPI.getCategoriesByTeacherUuid(teacherUuid);
        setIsCategoryLoading(false);

        if (!response || (response as { status?: string }).status === "failed") {
            setStatusPopUp({
                title: tID("failed"),
                message: tID("cannotLoadCategory"),
                type: "error",
            });
            return;
        }

        const rows = ((response as { data?: unknown }).data ?? response) as Array<Record<string, unknown>> | Record<string, unknown>;
        const list = Array.isArray(rows) ? rows : [];
        const names = list
            .map((item) => String(item?.name ?? "").trim())
            .filter(Boolean);

        setCategoryOptions(mergeWithCurrentValue(selectedCategory, names));
    };

    const GroupOptions = async () => {
        if (isGroupLoading) {
            return;
        }

        const teacherUuid = getTeacherUuidFromLocalStorage();
        if (!teacherUuid) {
            setStatusPopUp({
                title: tID("failed"),
                message: tID("noUserDataForGroup"),
                type: "error",
            });
            return;
        }

        setIsGroupLoading(true);
        const response = await categoryAndGroupAPI.getGroupsByTeacherUuid(teacherUuid);
        setIsGroupLoading(false);

        if (!response || (response as { status?: string }).status === "failed") {
            setStatusPopUp({
                title: tID("failed"),
                message: tID("cannotLoadGroup"),
                type: "error",
            });
            return;
        }

        const rows = ((response as { data?: unknown }).data ?? response) as Array<Record<string, unknown>> | Record<string, unknown>;
        const list = Array.isArray(rows) ? rows : [];
        const items = list
            .filter((item) => item?.id !== undefined && item?.id !== null)
            .map((item) => ({
                id: Number.isFinite(Number(item.id)) ? Number(item.id) : String(item.id),
                name: String(item?.name ?? "").trim(),
            }))
            .filter((item) => Boolean(item.name));

        const currentName = selectedGroup.trim();
        const currentId = detail?.groupId ?? null;
        if (currentName && !items.some((i) => i.name === currentName)) {
            items.unshift({ id: currentId !== null ? currentId : "", name: currentName });
        }

        setGroupOptions(items);
    };

    const categorySelectOptions = mergeWithCurrentValue(selectedCategory, categoryOptions);

    const foundGroupOption = groupOptions.find((o) => o.name === selectedGroup);
    const resolvedGroupId: number | string | null = foundGroupOption
        ? foundGroupOption.id
        : (detail?.groupId ?? null);

    const hasChanges =
        !isBannedRow && (
            thumbnailFile !== null ||
            selectedCategory !== String(detail?.category ?? "").trim() ||
            selectedGroup !== String(detail?.group ?? "").trim() ||
            editExerciseName !== String(detail?.exerciseName ?? "").trim() ||
            editDescription !== String(detail?.description ?? "").trim()
        );

    const buildThumbnailUrl = (pathOrUrl: string) => {
        const raw = String(pathOrUrl ?? "").trim();
        if (!raw) {
            return "";
        }

        if (raw.startsWith("http://") || raw.startsWith("https://")) {
            return raw;
        }

        const baseUrl = process.env.NEXT_PUBLIC_BACKEND_URL || "https://thaipilacreate.eef.or.th";
        const normalizedBaseUrl = baseUrl.replace(/\/$/, "");
        return `${normalizedBaseUrl}/api/${encodeURIComponent(raw)}`;
    };

    const handleCancelChanges = () => {
        setSelectedCategory(String(detail?.category ?? "").trim());
        setSelectedGroup(String(detail?.group ?? "").trim());
        setThumbnailFile(null);
        setThumbnailFileName("");
        setIsThumbnailEditorOpen(false);
        setEditExerciseName(String(detail?.exerciseName ?? "").trim());
        setIsEditingNameAndDesc(false);
        setEditDescription(String(detail?.description ?? "").trim());
    };

    const handleSaveAll = async () => {
        if (isBannedRow) {
            return;
        }

        if (!detail?.gameUuid) {
            setStatusPopUp({
                title: tID("failed"),
                message: tID("noGameDataForSave"),
                type: "error",
            });
            return;
        }

        const initialCat = String(detail?.category ?? "").trim();
        const initialGrp = String(detail?.group ?? "").trim();
        const initialExerciseName = String(detail?.exerciseName ?? "").trim();
        const initialDescription = String(detail?.description ?? "").trim();
        const thumbnailChanged = thumbnailFile !== null;
        const categoryChanged = selectedCategory !== initialCat;
        const groupChanged = selectedGroup !== initialGrp;
        const exerciseNameChanged = editExerciseName !== initialExerciseName;
        const descriptionChanged = editDescription !== initialDescription;

        if (!thumbnailChanged && !categoryChanged && !groupChanged && !exerciseNameChanged && !descriptionChanged) {
            return;
        }

        setIsSaving(true);
        let hasError = false;
        let newThumbnailUrl = localThumbnailUrl;

        if (thumbnailChanged) {
            const res = await mainAPI.updateThumnailByGameUuid({
                game_uuid: detail.gameUuid,
                thumbnail: thumbnailFile,
                thumbnailFileName: thumbnailFile!.name,
            });
            if (!res || (res as { status?: string }).status === "failed") {
                hasError = true;
            } else {
                const resRec = res as Record<string, unknown>;
                const resData = (resRec.data as Record<string, unknown> | undefined) ?? resRec;
                const nextRaw = String(resData.thumbnail_url ?? resData.thumbnailUrl ?? resData.thumbnail ?? "").trim();
                newThumbnailUrl = nextRaw ? buildThumbnailUrl(nextRaw) : URL.createObjectURL(thumbnailFile!);
            }
        }

        if (categoryChanged && !hasError) {
            const res = await mainAPI.updateSubjectByGameUuid({
                game_uuid: detail.gameUuid,
                subject: selectedCategory,
            });
            if (!res || (res as { status?: string }).status === "failed") {
                hasError = true;
            }
        }

        if (groupChanged && !hasError) {
            if (resolvedGroupId !== null && resolvedGroupId !== "") {
                const res = await mainAPI.updateGroupIdByGameUuid({
                    game_uuid: detail.gameUuid,
                    group_id: resolvedGroupId,
                });
                if (!res || (res as { status?: string }).status === "failed") {
                    hasError = true;
                }
            }
        }

        if ((exerciseNameChanged || descriptionChanged) && !hasError && detail.gameId != null) {
            const res = await mainAPI.updateExerciseNameAndSuggestion({
                gameId: detail.gameId,
                ...(exerciseNameChanged ? { exercise_name: editExerciseName } : {}),
                ...(descriptionChanged ? { description: editDescription } : {}),
            });
            if (!res || (res as { status?: string }).status === "failed") {
                hasError = true;
            }
        }

        setIsSaving(false);

        if (hasError) {
            setStatusPopUp({
                title: tID("failed"),
                message: tID("saveFailed"),
                type: "error",
            });
            return;
        }

        if (thumbnailChanged) {
            setLocalThumbnailUrl(newThumbnailUrl);
            setThumbnailFile(null);
            setThumbnailFileName("");
            setIsThumbnailEditorOpen(false);
            onThumbnailUpdated?.(detail.uniqueId, newThumbnailUrl);
        }

        if (exerciseNameChanged || descriptionChanged) {
            setIsEditingNameAndDesc(false);
        }

        onDetailUpdated?.(detail.uniqueId, {
            ...(thumbnailChanged ? { thumbnailUrl: newThumbnailUrl } : {}),
            ...(categoryChanged ? { category: selectedCategory } : {}),
            ...(groupChanged
                ? { group: selectedGroup, groupId: resolvedGroupId }
                : {}),
            ...(exerciseNameChanged ? { exerciseName: editExerciseName } : {}),
            ...(descriptionChanged ? { description: editDescription } : {}),
        });

        setStatusPopUp({
            title: tID("success"),
            message: tID("saveSuccess"),
            type: "success",
        });
    };

    if (!isOpen || !detail) {
        return null;
    }

    return (
        <>
            <div className="fixed inset-0 z-60 flex items-center justify-center bg-black/45 p-2 sm:p-4">
                <div className="max-h-[95vh] w-full max-w-[624px] overflow-y-auto rounded-md bg-[#F4F4F4] px-3 pb-4 pt-3 shadow-[0_6px_20px_rgba(0,0,0,0.35)] sm:px-4 sm:pb-5 sm:pt-4">
                    <div className="flex items-center justify-between">
                        <h2 className="text-lg font-bold leading-none text-[#4E4E4E] sm:text-2xl">{detail.subject || "-"}</h2>
                        <button
                            type="button"
                            onClick={onClose}
                            className="flex h-9 w-9 cursor-pointer items-center justify-center rounded-full bg-[#d5d9dd] text-[#8e949b] hover:bg-[#d5d9dd]/90"
                        >
                            <IoClose size={20} />
                        </button>
                    </div>

                    <div className="mt-3 space-y-2 text-sm sm:text-lg">
                        <div className="px-3 py-2 text-[#4E4E4E]">
                            <div className="text-lg">
                                <span>{tID("thumbnail")}</span>
                            </div>
                            <div className="rounded-md mt-2 flex justify-center cursor-pointer">
                                {displayThumbnailUrl ? (
                                    <PhotoProvider>
                                        <PhotoView src={displayThumbnailUrl}>
                                            <img
                                                src={displayThumbnailUrl}
                                                alt="thumbnail"
                                                width={429}
                                                height={239}
                                                className="h-auto w-full max-w-[429px] rounded-sm object-cover"
                                            />
                                        </PhotoView>
                                    </PhotoProvider>
                                ) : (
                                    <div className="flex aspect-[429/239] w-full max-w-[429px] items-center justify-center rounded-sm border border-[#d9d9d9] bg-[#f5f5f5] text-base text-[#9b9b9b]">
                                        {tID("noImage")}
                                    </div>
                                )}
                            </div>

                            {isThumbnailEditorOpen ? (
                                <div className="mt-3">
                                    <label className="block">
                                        <span className="text-base font-medium text-[#4E4E4E]">
                                            {tID("thumbnailRecommend")}
                                        </span>
                                        <div className="mt-1 relative">
                                            <input
                                                type="file"
                                                accept=".jpg,.jpeg,.png,image/jpeg,image/png"
                                                onChange={(event) => {
                                                    const file = event.target.files?.[0];
                                                    if (!file) {
                                                        setThumbnailFileName("");
                                                        setThumbnailFile(null);
                                                        event.currentTarget.value = "";
                                                        return;
                                                    }

                                                    if (!validateImageFile(file)) {
                                                        setValidationMessage(getMediaValidationErrorMessage("image", locale));
                                                        event.currentTarget.value = "";
                                                        return;
                                                    }

                                                    setThumbnailFileName(file.name);
                                                    setThumbnailFile(file);
                                                    event.currentTarget.value = "";
                                                }}
                                                className="absolute inset-0 h-full w-full cursor-pointer opacity-0"
                                            />
                                            <div className="flex h-9 w-full items-center justify-between rounded-lg border border-[#bfc2c8] bg-white px-3 text-base">
                                                <span
                                                    className={`truncate text-sm ${thumbnailFileName ? "text-[#666]" : "text-[#9b9b9b]"}`}
                                                >
                                                    {thumbnailFileName || tID("changeThumbnail")}
                                                </span>
                                                <FaRegFolder className="shrink-0 text-xl text-[#666]" />
                                            </div>
                                        </div>
                                    </label>

                                    <div className="mt-3 flex items-center justify-center gap-3">
                                        <button
                                            type="button"
                                            onClick={() => {
                                                setIsThumbnailEditorOpen(false);
                                                setThumbnailFile(null);
                                                setThumbnailFileName("");
                                            }}
                                            className="min-w-20 cursor-pointer rounded-md border border-[#c4c4c4] bg-white px-7 py-2 text-base font-semibold text-[#4E4E4E]"
                                        >
                                            {tID("cancel")}
                                        </button>
                                    </div>
                                </div>
                            ) : (
                                <button
                                    type="button"
                                    onClick={() => {
                                        if (isBannedRow) {
                                            return;
                                        }
                                        setIsThumbnailEditorOpen(true);
                                    }}
                                    disabled={isBannedRow}
                                    className={`mt-3 flex w-full items-center justify-center text-center text-base font-medium ${isBannedRow ? "cursor-not-allowed text-[#9ca3af]" : "cursor-pointer text-[#0092D7]"}`}
                                >
                                    <FiEdit3 size={22} className="mr-2" /> {tID("editThumbnail")}
                                </button>
                            )}

                        </div>

                        <DetailLine label={tID("createdAt")} value={formatCompactDateTime(detail.createdAt)} />
                        <DetailLine label={tID("updatedAt")} value={formatCompactDateTime(detail.updatedAt)} />
                        <DetailLine label={tID("createdBy")} value={detail.createdBy || "-"} />

                        <div className="rounded-sm bg-white px-3 py-2 text-[#4E4E4E] space-y-2">
                            <div className="grid grid-cols-1 items-start gap-2 text-sm sm:grid-cols-[140px_1fr_auto] sm:items-center sm:text-lg">
                                <span>{sourceLabel}</span>
                                {isEditingNameAndDesc ? (
                                    <input
                                        type="text"
                                        value={editExerciseName}
                                        onChange={(e) => setEditExerciseName(e.target.value)}
                                        className="h-9 w-full rounded-lg border border-[#bfc2c8] bg-white px-2 text-base outline-none"
                                        autoFocus
                                    />
                                ) : (
                                    <span className="wrap-break-word break-all">{editExerciseName || "-"}</span>
                                )}
                                <button
                                    type="button"
                                    onClick={() => {
                                        if (isBannedRow) {
                                            return;
                                        }
                                        setIsEditingNameAndDesc((prev) => !prev);
                                    }}
                                    disabled={isBannedRow}
                                    className={`flex items-center gap-1 text-base font-medium ${isBannedRow ? "cursor-not-allowed text-[#9ca3af]" : "cursor-pointer text-[#0092D7] hover:text-[#0078b5]"}`}
                                >
                                    <FiEdit3 size={16} />
                                    <span>{tID("edit")}</span>
                                </button>
                            </div>
                            <div className="grid grid-cols-1 items-start gap-1 text-sm sm:grid-cols-[140px_1fr] sm:gap-2 sm:text-lg">
                                <span>{tID("activityDescription")}</span>
                                {isEditingNameAndDesc ? (
                                    <textarea
                                        value={editDescription}
                                        onChange={(e) => setEditDescription(e.target.value)}
                                        rows={3}
                                        className="w-full rounded-lg border border-[#bfc2c8] bg-white px-2 py-1 text-base outline-none resize-none"
                                    />
                                ) : (
                                    <span className="wrap-break-word whitespace-pre-line break-all">{editDescription || "-"}</span>
                                )}
                            </div>
                        </div>

                        <div className="rounded-sm bg-white px-3 py-2 text-[#4E4E4E]">
                            <div className="grid grid-cols-1 items-start gap-1 text-sm sm:grid-cols-[140px_1fr] sm:gap-2 sm:text-lg">
                                <span>{tID("category")}</span>
                                <div className="relative">
                                    <select
                                        value={selectedCategory}
                                        onChange={(event) => setSelectedCategory(event.target.value)}
                                        onFocus={() => {
                                            if (isBannedRow) {
                                                return;
                                            }
                                            void CategoryOptions();
                                        }}
                                        disabled={isBannedRow}
                                        className={`h-9 w-full appearance-none rounded-lg border border-[#bfc2c8] bg-white px-2 pr-9 text-base outline-none ${isBannedRow ? "cursor-not-allowed text-[#9ca3af]" : "cursor-pointer"}`}
                                    >
                                        {categorySelectOptions.length > 0 ? (
                                            categorySelectOptions.map((option) => (
                                                <option key={option} value={option}>
                                                    {option}
                                                </option>
                                            ))
                                        ) : (
                                            <option value="">-</option>
                                        )}
                                    </select>
                                    <span className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-gray-500">
                                        <MdKeyboardArrowDown size={20} />
                                    </span>
                                </div>
                            </div>
                        </div>

                        <div className="rounded-sm bg-white px-3 py-2 text-[#4E4E4E]">
                            <div className="grid grid-cols-1 items-start gap-1 text-sm sm:grid-cols-[140px_1fr] sm:gap-2 sm:text-lg">
                                <span>{tID("group")}</span>
                                <div className="relative">
                                    <select
                                        value={selectedGroup}
                                        onChange={(event) => setSelectedGroup(event.target.value)}
                                        onFocus={() => {
                                            if (isBannedRow) {
                                                return;
                                            }
                                            void GroupOptions();
                                        }}
                                        disabled={isBannedRow}
                                        className={`h-9 w-full appearance-none rounded-lg border border-[#bfc2c8] bg-white px-2 pr-9 text-base outline-none ${isBannedRow ? "cursor-not-allowed text-[#9ca3af]" : "cursor-pointer"}`}
                                    >
                                        {groupOptions.length > 0 ? (
                                            groupOptions.map((option) => (
                                                <option key={String(option.id)} value={option.name}>
                                                    {option.name}
                                                </option>
                                            ))
                                        ) : (
                                            <option value={selectedGroup || ""}>{selectedGroup || "-"}</option>
                                        )}
                                    </select>
                                    <span className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-gray-500">
                                        <MdKeyboardArrowDown size={20} />
                                    </span>
                                </div>
                            </div>
                        </div>

                    </div>

                    <div className="mt-3 rounded-sm bg-white px-3 py-2">
                        <div className="flex items-center justify-between text-[#4E4E4E]">
                            <span className="text-lg">{tID("saveNoted")}</span>
                            <button
                                type="button"
                                onClick={() => {
                                    if (isBannedRow) {
                                        return;
                                    }
                                    onCreateNote(detail);
                                }}
                                disabled={isBannedRow}
                                className={`inline-flex items-center gap-1.5 text-[26px] ${isBannedRow ? "cursor-not-allowed text-[#9ca3af]" : "cursor-pointer text-[#06A7E4] hover:text-[#008cc3]"}`}
                            >
                                <TbNotebook />
                                <span className="text-base font-medium">{tID("addNote")}</span>
                            </button>
                        </div>

                        {noteRows.length === 0 ? (
                            <div className="py-4 text-center text-lg text-[#9b9b9b]">{tID("noNote")}</div>
                        ) : (
                            <div className="mt-1 space-y-1.5">
                                {noteRows.map((note) => (
                                    <div key={String(note.id)} className="grid grid-cols-[90px_1fr_auto] items-center gap-2 text-[#4E4E4E] p-1">
                                        <span className="text-lg leading-none">{formatCompactDate(note.create_at)}</span>
                                        <div>
                                            <div className="text-lg leading-none">{note.name || "-"}</div>
                                            {/* <div className="mt-1 text-lg leading-snug text-[#6a6a6a]">{note.detail || "-"}</div> */}
                                        </div>
                                        <div className="flex items-center gap-3 pr-1 text-[#06A7E4]">
                                            <button
                                                type="button"
                                                onClick={() => onEditNote(detail, note)}
                                                className="cursor-pointer hover:text-[#008cc3]"
                                                title="Edit note"
                                            >
                                                <FiEdit3 size={22} />
                                            </button>
                                            <button
                                                type="button"
                                                onClick={() => setDeleteTargetNote(note)}
                                                className="cursor-pointer text-[#f35a5a] hover:text-[#d43f3f]"
                                                title="Delete note"
                                            >
                                                <RiDeleteBinLine size={22} />
                                            </button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>

                    <div className="mt-4 flex items-center justify-center gap-3">
                        {/* <button
                            type="button"
                            onClick={handleCancelChanges}
                            disabled={isSaving || !hasChanges}
                            className="min-w-24 cursor-pointer rounded-md border border-[#c4c4c4] bg-white px-7 py-2 text-base font-semibold text-[#4E4E4E] disabled:cursor-not-allowed disabled:opacity-40"
                        >
                            ยกเลิก
                        </button> */}
                        <button
                            type="button"
                            onClick={() => { void handleSaveAll(); }}
                            disabled={isSaving || !hasChanges}
                            className="h-10 min-w-24 cursor-pointer rounded-md bg-[#1094d4] px-6 text-base font-semibold text-white hover:bg-[#0a9cf0] disabled:cursor-not-allowed disabled:opacity-40"
                        >
                            {isSaving ? tID("saving") : tID("save")}
                        </button>
                    </div>
                </div>
            </div>

            {deleteTargetNote ? (
                <ConfirmPopUp
                    title={tID("confirmDelete")}
                    message={tID("deleteNoteConfirmation")}
                    confirmText={tID("ok")}
                    cancelText={tID("cancel")}
                    onConfirm={() => {
                        void handleDeleteNote();
                    }}
                    onCancel={() => {
                        if (!isDeletingNote) {
                            setDeleteTargetNote(null);
                        }
                    }}
                    isConfirming={isDeletingNote}
                />
            ) : null}

            {statusPopUp ? (
                <StatusPopUp
                    title={statusPopUp.title}
                    message={statusPopUp.message}
                    type={statusPopUp.type}
                    onClose={() => setStatusPopUp(null)}
                />
            ) : null}

            {validationMessage ? (
                <StatusPopUp
                    title={getMediaValidationTitle(locale)}
                    message={validationMessage}
                    type="error"
                    onClose={() => setValidationMessage(null)}
                />
            ) : null}
        </>
    );
}
